/**
 * chain-eos/multisig.ts
 *
 * eosio.msig proposal manager: propose / approve / unapprove / exec / cancel,
 * plus a read-only proposal list with an "auth risk" flag (does this
 * proposal touch updateauth/linkauth/deleteauth/unlinkauth?) — the same
 * signal blocksfinity.com's /msigs page surfaces before you approve
 * something blind.
 *
 * `propose` needs the *inner* transaction's actions serialized against
 * their contracts' ABIs (a different transaction than the one carrying the
 * `propose` action itself). Rather than re-implementing ABI-aware action
 * encoding here, we let `session.transact(..., { broadcast: false })`
 * resolve+encode it for us and reuse the resulting `resolved.transaction` —
 * WharfKit already knows how to turn `{account, name, data}` into a
 * correctly-serialized action.
 */

import { Name, Serializer, Transaction } from '@wharfkit/antelope'
import type { Session } from './session.js'
import { EOS_RPC_ENDPOINTS } from './config.js'

const MSIG_CONTRACT = 'eosio.msig'

/** Action names that change who controls an account — worth flagging before approval. */
const AUTH_RISK_ACTIONS = new Set(['updateauth', 'deleteauth', 'linkauth', 'unlinkauth'])

export interface MsigActionInput {
  account: string
  name: string
  authorization: { actor: string; permission: string }[]
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any
}

export interface ProposeMsigParams {
  proposer: string
  proposalName: string
  requested: { actor: string; permission: string }[]
  actions: MsigActionInput[]
  /** Seconds until the proposal expires if not fully approved. Default 7 days. */
  expireSeconds?: number
  /** Which permission on `proposer` signs the propose action. */
  signingPermission: string
}

export async function proposeMsig(session: Session, params: ProposeMsigParams): Promise<string> {
  // Build the inner transaction via a dry-run transact — this gets us
  // ABI-correct serialized action data without duplicating WharfKit's ABI
  // resolution logic.
  const built = await session.transact({ actions: params.actions }, { broadcast: false })
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const b = built as any
  const trx = b.resolved?.transaction ?? b.transaction
  if (!trx) throw new Error('Could not build the proposal transaction')

  const result = await session.transact({
    action: {
      account: MSIG_CONTRACT,
      name: 'propose',
      authorization: [{ actor: params.proposer, permission: params.signingPermission }],
      data: {
        proposer: Name.from(params.proposer),
        proposal_name: Name.from(params.proposalName),
        requested: params.requested,
        trx,
      },
    },
  })

  return (result.response as { transaction_id?: string })?.transaction_id ?? ''
}

async function simpleMsigAction(
  session: Session,
  actor: string,
  signingPermission: string,
  name: 'approve' | 'unapprove' | 'cancel',
  proposer: string,
  proposalName: string,
  extraData: Record<string, unknown> = {},
): Promise<string> {
  const result = await session.transact({
    action: {
      account: MSIG_CONTRACT,
      name,
      authorization: [{ actor, permission: signingPermission }],
      data: {
        proposer: Name.from(proposer),
        proposal_name: Name.from(proposalName),
        ...extraData,
      },
    },
  })
  return (result.response as { transaction_id?: string })?.transaction_id ?? ''
}

export async function approveMsig(
  session: Session,
  proposer: string,
  proposalName: string,
  approver: string,
  signingPermission: string,
): Promise<string> {
  return simpleMsigAction(session, approver, signingPermission, 'approve', proposer, proposalName, {
    level: { actor: Name.from(approver), permission: signingPermission },
  })
}

export async function unapproveMsig(
  session: Session,
  proposer: string,
  proposalName: string,
  approver: string,
  signingPermission: string,
): Promise<string> {
  return simpleMsigAction(session, approver, signingPermission, 'unapprove', proposer, proposalName, {
    level: { actor: Name.from(approver), permission: signingPermission },
  })
}

export async function cancelMsig(
  session: Session,
  proposer: string,
  proposalName: string,
  canceler: string,
  signingPermission: string,
): Promise<string> {
  return simpleMsigAction(session, canceler, signingPermission, 'cancel', proposer, proposalName)
}

export async function execMsig(
  session: Session,
  proposer: string,
  proposalName: string,
  executer: string,
  signingPermission: string,
): Promise<string> {
  const result = await session.transact({
    action: {
      account: MSIG_CONTRACT,
      name: 'exec',
      authorization: [{ actor: executer, permission: signingPermission }],
      data: {
        proposer: Name.from(proposer),
        proposal_name: Name.from(proposalName),
        executer: Name.from(executer),
      },
    },
  })
  return (result.response as { transaction_id?: string })?.transaction_id ?? ''
}

// ─── Reading proposals ─────────────────────────────────────────────────────

export interface MsigProposalSummary {
  proposer: string
  proposalName: string
  expiration: string
  actions: { account: string; name: string }[]
  requestedApprovals: { actor: string; permission: string }[]
  providedApprovals: { actor: string; permission: string }[]
  hasAuthRisk: boolean
}

interface ProposalRow {
  proposal_name: string
  packed_transaction: string // hex
}

interface Approvals2Row {
  proposal_name: string
  requested_approvals: { level: { actor: string; permission: string }; time?: string }[]
  provided_approvals: { level: { actor: string; permission: string }; time?: string }[]
}

async function fetchTableRows<T>(
  code: string,
  scope: string,
  table: string,
): Promise<T[]> {
  for (const url of EOS_RPC_ENDPOINTS) {
    try {
      const res = await fetch(`${url}/v1/chain/get_table_rows`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ json: true, code, scope, table, limit: 100 }),
      })
      if (!res.ok) continue
      const data = (await res.json()) as { rows?: T[] }
      return data.rows ?? []
    } catch {
      // try next endpoint
    }
  }
  return []
}

/** Lists open proposals made BY `proposer` (eosio.msig scopes `proposal`/`approvals2` by proposer). */
export async function listMsigProposalsByProposer(proposer: string): Promise<MsigProposalSummary[]> {
  const [proposals, approvals] = await Promise.all([
    fetchTableRows<ProposalRow>(MSIG_CONTRACT, proposer, 'proposal'),
    fetchTableRows<Approvals2Row>(MSIG_CONTRACT, proposer, 'approvals2'),
  ])

  const approvalsByName = new Map(approvals.map(a => [a.proposal_name, a]))

  return proposals.map(row => {
    let actions: { account: string; name: string }[] = []
    let expiration = ''
    try {
      const trx = Serializer.decode({ data: row.packed_transaction, type: Transaction })
      expiration = String(trx.expiration)
      actions = trx.actions.map(a => ({ account: String(a.account), name: String(a.name) }))
    } catch (e) {
      console.warn('[multisig] could not decode packed_transaction for', row.proposal_name, e)
    }

    const appr = approvalsByName.get(row.proposal_name)

    return {
      proposer,
      proposalName: row.proposal_name,
      expiration,
      actions,
      requestedApprovals: (appr?.requested_approvals ?? []).map(r => ({
        actor: r.level.actor,
        permission: r.level.permission,
      })),
      providedApprovals: (appr?.provided_approvals ?? []).map(p => ({
        actor: p.level.actor,
        permission: p.level.permission,
      })),
      hasAuthRisk: actions.some(a => AUTH_RISK_ACTIONS.has(a.name)),
    }
  })
}
